import React from 'react';
import { Slider as BaseSlider } from "../../../shared/components";
import { DocumentedComponent } from '../../../shared/components/DocumentedComponent';
import { useAppSelector } from "../../../store";
import { useAppActions } from "../../../shared/hooks";

interface Props {
    name: string;
}

function Slider(props: Props) {
    const { name, ...rest } = props;
    const parameterValues = useAppSelector(state => state.storyline.parameterValues);
    const { updateParameterValue } = useAppActions();
    const [value, setValue] = React.useState(parameterValues.get(name) as number | number[] || 0);

    const handleChange = (event: any, newValue: number | number[]) => {
        updateParameterValue(name, newValue);
        setValue(newValue);
    };

    React.useEffect(() => {
        if (parameterValues.has(name)) {
            const newValue = parameterValues.get(name) as number | number[];
            if (newValue !== value) {
                setValue(newValue);
            }
        }
    }, [parameterValues]);

    return (
        <BaseSlider
            valueLabelDisplay="on"
            color="primary"
            name={name}
            {...rest}
            value={value}
            onChange={(_event, newValue) => setValue(newValue)}
            onChangeCommitted={handleChange}
        />
    );
}

(Slider as DocumentedComponent).metadata = {
    description: "The `Slider` control allows the user to select a numeric value within a range.  Min/max values and step sizes can be configured.",
    isSelfClosing: true,
    attributes: [
        { name: "name", type: "string", description: "The name of the variable that the selected value will be read from and persisted to. The underlying component's `data-testid` attribute will be set to this value. This is useful for targeting the element via Playwright, Puppeteer or similar UI testing tools. Can be overridden via an explicit `data-testid` value." },
        { name: "min", type: "number", description: "The minimum value that can be selected." },
        { name: "max", type: "number", description: "The maximum value that can be selected." },
        { name: "step", type: "number", description: "The increment by which the value can be changed." },
        { name: "orientation", type: "string", options: ["horizontal", "vertical"], description: "The orientation of the slider. Can be either `horizontal` or `vertical`. Optional - defaults to `horizontal`." },
    ]
};

export default Slider;
